import { Icon } from "../../icons";
import { alignClass, columnWidth, isSortable } from "./columnLayout";
import type { Column, SortDirection, SortState } from "./DataTableTypes";

const ARIA_SORT: Record<SortDirection, "ascending" | "descending"> = { asc: "ascending", desc: "descending" };

interface DataTableHeadProps<R> {
  columns: Column<R>[];
  sort: SortState | undefined;
  onSort: (key: string) => void;
}

/** Column widths and header cells of the table. */
export function DataTableHead<R>({ columns, sort, onSort }: DataTableHeadProps<R>): React.JSX.Element {
  return (
    <>
      <colgroup>
        {columns.map((col) => {
          const width = columnWidth(col);
          return <col key={col.key} style={width ? { width } : undefined} />;
        })}
      </colgroup>
      <thead>
        <tr>
          {columns.map((col) => {
            const direction = sort?.key === col.key ? sort.dir : undefined;
            return (
              <th key={col.key} scope="col" className={alignClass(col)} aria-sort={direction ? ARIA_SORT[direction] : undefined}>
                <HeaderLabel col={col} onSort={onSort} />
              </th>
            );
          })}
        </tr>
      </thead>
    </>
  );
}

/** Actions columns get a screen-reader-only label; sortable columns a sort button. */
function HeaderLabel<R>({ col, onSort }: { col: Column<R>; onSort: (key: string) => void }): React.JSX.Element {
  if (col.type === "actions") return <span className="nb-sr">{col.label}</span>;
  if (!isSortable(col)) return <>{col.label}</>;
  return (
    <button type="button" className="nb-th-btn" onClick={() => onSort(col.key)} title={`Sort by ${col.label.toLowerCase()}`}>
      <span>{col.label}</span>
      <span className="nb-sort" aria-hidden="true">
        <Icon name="arrowUp" />
      </span>
    </button>
  );
}
